import {
  Box,
  Chip,
  Icon,
  Image,
  Typography,
  useTheme,
} from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import {
  ActivityListing_ListingFragment,
  ListingCard_ListingFragment,
} from "@outschool/gql-frontend-generated";
import { faShare } from "@outschool/icons";
import { useLocalPriceDisplay, useTranslation } from "@outschool/localization";
import { measurePerformance, useProductClicked } from "@outschool/ui-analytics";
import {
  TrackedButton,
  UserHeadshotImage,
} from "@outschool/ui-components-shared";
import {
  SimpleRatings,
  useActivityString,
} from "@outschool/ui-components-website";
import { Space } from "@outschool/ui-legacy-component-library";
import { useLinkComponent, useNavigation } from "@outschool/ui-utils";
import * as React from "react";

import { useEsaUserWithoutPaidMembership } from "../../../hooks/useEsaUserWithoutPaidMembership";
import { FreeForMembersChip } from "../../Chips/FreeForMembersChip";
import { SaveHeart } from "../../saving/SaveButton";
import { CoinIcon } from "../../subscriptions/CoinIcon";
import { useMembershipRequirement } from "../../subscriptions/hooks/useMembershipRequirement";
import Stars from "../components/Stars";
import TileInfoBox, { TileInfoBoxSimplified } from "../components/TileInfoBox";
import { TileInfoName } from "./CardProps";
import {
  CARD_COLUMN_WIDTH,
  CARD_MARGIN_RIGHT,
  MIN_CARD_HEIGHT,
  SIMPLIFIED_CARD_IMAGE_HEIGHT,
  SIMPLIFIED_CARD_IMAGE_WIDTH,
  SIMPLIFIED_MIN_SIMPLIFIED_CARD_WIDTH,
} from "./constants";

import type { CardProps } from "./CardProps";

function isActivityListing(
  activity: unknown,
): activity is ActivityListing_ListingFragment["activity"] {
  return (
    (activity as ActivityListing_ListingFragment["activity"]).syllabus !== null
  );
}

export const GroupClassCard = ({
  activity,
  disabled,
  hideHeart,
  isLastColumn,
  isSavedByUser,
  leader,
  trackingProperties,
  variant,
  openInNewTab = false,
  listingIndex = -1,
  linkTo,

  isCrossSell = false,
  tiles = [
    TileInfoName.Age,
    TileInfoName.CapacityOrDuration,
    TileInfoName.Price,
  ],
  customOnClick,
  shareButtonFunction,
  isCurrentlyViewing,
  overrideShowCredits = false,
  columnWidth,
}: CardProps) => {
  const Link = useLinkComponent();
  const theme = useTheme();
  const { t } = useTranslation("client\\components\\listings\\ListingCard");
  const trackProductClicked = useProductClicked();

  const { ageRangeString } = useActivityString();
  const { makePerMeetingPriceText, makePerMeetingPriceCreditsText } =
    useLocalPriceDisplay();
  const { isEsaUserWithoutPaidMembership } = useEsaUserWithoutPaidMembership();

  const cardLinkTo = linkTo || activity.path;

  const navigate = useNavigation();
  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (leader) {
        trackProductClicked({ activity, leader }, listingIndex);
      }

      if (customOnClick) {
        e.preventDefault();
        customOnClick(e);
        return;
      }

      if (openInNewTab) {
        e.preventDefault();
        navigate(cardLinkTo, {
          newTab: openInNewTab,
        });
      }
    },
    [
      cardLinkTo,
      navigate,
      openInNewTab,
      trackProductClicked,
      activity,
      leader,
      listingIndex,
      customOnClick,
    ],
  );

  const { showCredits: shouldShowCredits } = useMembershipRequirement();
  const showCredits = shouldShowCredits || !!overrideShowCredits;

  const topPriceText = showCredits
    ? makePerMeetingPriceCreditsText(activity)
    : makePerMeetingPriceText(activity);
  const bottomPriceText = activity.isClub
    ? t`Per month`
    : activity.isFlexSchedule
    ? t`Per week`
    : t`Per class`;

  function ageRange() {
    return t("Ages") + " " + activity.age_min + "-" + activity.age_max;
  }

  let lessonCount: null | number = null;
  if (isActivityListing(activity) && activity.syllabus?.lessons.length) {
    lessonCount = activity.syllabus.lessons.length;
  }

  const cardColumnWidth = columnWidth || CARD_COLUMN_WIDTH;
  const isTutor = activity.size_max === 1;
  const tutorText = t("1-on-1");
  const regularGroupClassCard = (
    <Box sx={{ position: "relative" }}>
      {shareButtonFunction && (
        <TrackedButton
          trackingName="carousel card share button clicked"
          trackingUniqueId={activity?.uid}
          variant="outlined"
          onClick={shareButtonFunction}
          sx={{
            position: "absolute",
            left: cardColumnWidth - 50,
            top: 5,
            width: 30,
            padding: "0.7rem 1.9rem",
          }}
        >
          <Icon icon={faShare} size="medium" />
        </TrackedButton>
      )}
      <Link
        style={{
          display: "flex",
          width: cardColumnWidth,
          position: "relative",
          justifyContent: isLastColumn ? "flex-end" : "flex-start",
          cursor: disabled ? "default" : "pointer",
          marginRight: CARD_MARGIN_RIGHT,
          textDecoration: "none",
        }}
        to={cardLinkTo}
        {...trackingProperties}
        disabled={disabled}
        onClick={onClick}
        reloadDocument
        trackViews
      >
        <Box
          sx={{
            backgroundColor: "#FFF",
            border: "1px solid #DDDFE3",
            borderRadius: 8,
            color: "#2E3238",
            height: MIN_CARD_HEIGHT,
            width: cardColumnWidth,

            "&:hover": {
              boxShadow: disabled ? "none" : "0 2px 10px rgba(0, 0, 0, 0.25)",
            },
          }}
        >
          <Image
            height={140}
            width={cardColumnWidth - 2}
            sx={{
              borderRadius: "8px 8px 0 0",
            }}
            src={`${FilestackUrls.imageUrl(activity.details.photo, {
              w: cardColumnWidth - 2,
              h: 140,
            })}`}
            onLoad={() => {
              return listingIndex === 0
                ? measurePerformance("os-page-load", {
                    source: "image",
                    variant,
                  })
                : null;
            }}
          />
          {isCurrentlyViewing && (
            <Chip
              color="neutral"
              size="small"
              label={t("Currently Viewing")}
              sx={{ position: "absolute", left: 9, top: 9 }}
            />
          )}
          <Box
            sx={{
              padding: "8px 16px 16px 16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: !activity.isIncludedWithMembership
                  ? "flex-start"
                  : "center",
                justifyContent: "space-between",
                mb: 5,
              }}
            >
              {activity.isIncludedWithMembership ? (
                <FreeForMembersChip />
              ) : (
                <Box
                  sx={{
                    fontSize: 16,
                    fontWeight: "fontWeightBold",
                    display: "-webkit-box",
                    WebkitLineClamp: "3",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    maxHeight: "calc(1.5em * 3)",
                    marginBottom: "12px",
                    marginRight: "4px",
                  }}
                >
                  {activity.titleTranslated}
                </Box>
              )}
              {!hideHeart && (
                <SaveHeart
                  activity={activity}
                  disabled={disabled}
                  isSet={isSavedByUser}
                  trackingProperties={trackingProperties}
                  sx={{
                    fontSize: 24,
                    height: 24,
                    width: 24,
                  }}
                />
              )}
            </Box>
            {activity.isIncludedWithMembership && (
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: "fontWeightBold",
                  display: "-webkit-box",
                  WebkitLineClamp: "3",
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  maxHeight: "calc(1.5em * 3)",
                  marginBottom: "12px",
                  marginRight: "4px",
                }}
              >
                {activity.titleTranslated}
              </Box>
            )}
            {!!leader && (
              <Box>
                <Box
                  flex
                  sx={{
                    alignItems: "center",
                    height: "42px",
                    paddingBottom: "1em",
                  }}
                >
                  <Box sx={{ paddingRight: ".5em" }}>
                    <UserHeadshotImage
                      user={leader}
                      size={32}
                      sx={{
                        border: "1px solid",
                        borderColor: "gray200",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        maxHeight: "48px",
                        display: "-webkit-box",
                        WebkitLineClamp: "2",
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {leader.name}
                    </Box>
                  </Box>
                </Box>
                {leader.reviewCount && (
                  <SimpleRatings
                    starRating={leader.averageActivityStarRating}
                    reviewCount={leader.reviewCount}
                  />
                )}
              </Box>
            )}
            <Box
              sx={{
                width: "calc(100% - 16px)",
                position: "absolute",
                bottom: "12px",
                left: "0px",
                marginX: "8px",
              }}
            >
              <Box
                flex
                sx={{
                  flexDirection: "row",
                  gap: "8px",
                  marginTop: "12px",
                }}
              >
                {tiles.includes(TileInfoName.Age) && (
                  <TileInfoBox
                    topText={ageRangeString(activity, false)}
                    bottomText={t("Ages")}
                  />
                )}
                {tiles.includes(TileInfoName.CapacityOrDuration) &&
                activity.isClub ? (
                  <TileInfoBox
                    topText={
                      (
                        activity as ListingCard_ListingFragment["activity"]
                      ).clubSection?.filledSpaceCount.toString() || ""
                    }
                    bottomText={t("Learners")}
                  />
                ) : (
                  activity.duration_minutes && (
                    <TileInfoBox
                      topText={activity.duration_minutes.toString()}
                      bottomText={t("Mins")}
                    />
                  )
                )}

                {tiles.includes(TileInfoName.Price) && (
                  <TileInfoBox
                    sx={{
                      whiteSpace: "nowrap",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                    topText={
                      showCredits ? (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignSelf: "center",
                              marginRight: 4,
                            }}
                          >
                            <CoinIcon
                              height={16}
                              width={16}
                              fillColor={theme.palette.warning.main}
                              strokeColor={theme.palette.warning[700]!}
                            />
                          </Box>
                          {activity.isIncludedWithMembership &&
                          !isEsaUserWithoutPaidMembership
                            ? "0"
                            : topPriceText}
                        </Box>
                      ) : (
                        topPriceText
                      )
                    }
                    bottomText={bottomPriceText}
                  />
                )}
                {tiles.includes(TileInfoName.Lessons) &&
                  lessonCount !== null && (
                    <TileInfoBox topText={lessonCount + " " + t`Lessons`} />
                  )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Link>
    </Box>
  );

  const simplifiedGroupClassCard = (
    <Box sx={{ position: "relative" }}>
      {shareButtonFunction && (
        <TrackedButton
          trackingName="carousel card share button clicked"
          trackingUniqueId={activity?.uid}
          variant="outlined"
          onClick={shareButtonFunction}
          sx={{
            position: "absolute",
            left: SIMPLIFIED_CARD_IMAGE_WIDTH - 50,
            top: 5,
            width: 30,
            padding: "0.7rem 1.9rem",
          }}
        >
          <Icon icon={faShare} size="medium" />
        </TrackedButton>
      )}
      <Link
        style={{
          cursor: disabled ? "default" : "pointer",
          textDecoration: "none",
        }}
        to={cardLinkTo}
        {...trackingProperties}
        disabled={disabled}
        onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          if (leader) {
            trackProductClicked({ activity, leader }, listingIndex);
          }
          if (customOnClick) {
            customOnClick(e);
          }
        }}
        reloadDocument
        trackViews
      >
        <Box
          sx={{
            backgroundColor: "transparent",
            color: "#2E3238",
            width: SIMPLIFIED_MIN_SIMPLIFIED_CARD_WIDTH,
            marginRight: "16px",
          }}
        >
          <Image
            height={SIMPLIFIED_CARD_IMAGE_HEIGHT}
            width={SIMPLIFIED_CARD_IMAGE_WIDTH}
            sx={{
              borderRadius: "8px",
              height: SIMPLIFIED_CARD_IMAGE_HEIGHT,
              width: SIMPLIFIED_CARD_IMAGE_WIDTH,
            }}
            src={`${FilestackUrls.imageUrl(activity.details.photo)}`}
            onLoad={() => {
              return listingIndex === 0
                ? measurePerformance("os-page-load", {
                    source: "image",
                    variant,
                  })
                : null;
            }}
          />
          <Space y={"4px"}></Space>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              WebkitLineClamp: 4,
            }}
          >
            <Typography as={Box} variant="subtitle2">
              {activity.titleTranslated}
            </Typography>
          </Box>
          <Space y={"6px"}></Space>
          <Box flex sx={{ gap: "8px", alignItems: "center" }}>
            {!!leader && !!activity?.reviewCount && (
              <>
                <Typography as={Box} variant="body2">
                  <Stars
                    activity={activity}
                    leader={leader}
                    isMobile
                    isSimplified
                  />
                </Typography>
                <Box>·</Box>
              </>
            )}
            <Typography
              as={Box}
              variant="body2"
              sx={{
                color: "gray900",
              }}
            >
              {ageRange()}
            </Typography>
          </Box>
          <Space y={"6px"}></Space>
          <Box flex>
            <TileInfoBoxSimplified
              price={topPriceText}
              isTutor={isTutor}
              isCreditPrice={showCredits}
              sx={{
                paddingRight: "6px",
              }}
            />
            {isTutor && (
              <Box
                flex
                sx={(theme: any) => ({
                  padding: "0 4px",
                  borderRadius: "4px",
                  backgroundColor: `${theme.palette.primary[50]}`,
                  color: `${theme.palette.primary[600]}`,
                  fontSize: `${theme.typography.pxToRem(13)}`,
                  lineHeight: "18px",
                  letterSpacing: "0.2px",
                  alignItems: "center",
                })}
              >
                {tutorText}
              </Box>
            )}
          </Box>
        </Box>
      </Link>
    </Box>
  );

  return isCrossSell ? simplifiedGroupClassCard : regularGroupClassCard;
};
